import { vec2 } from 'gl-matrix';
import { PlaneView } from '../../view/views/PlaneView';
import { MOUSE_BUTTON_RIGHT, MOUSE_BUTTONS_RIGHT_MASK } from '../MouseButton';
import { ModifierKeys } from '../ModifierKeys';
import { PointerLockManager } from '../PointerLockManager';
import { GlobalCoordinator } from './GlobalCoordinator';
import { WindowLevelDrag } from './WindowLevelDrag';

/**
 * Configuration for the right mouse button gesture.
 */
const RIGHT_BUTTON_CONFIG = {
    /** Pixels of movement before a press becomes a drag
     *  (distinguishes stationary right-click from
     *  right-click-drag). */
    activationThreshold: 3,
    /** The button this coordinator owns. */
    mouseButton: MOUSE_BUTTON_RIGHT,
    /** Bitmask of that button in MouseEvent.buttons. */
    mouseButtonMask: MOUSE_BUTTONS_RIGHT_MASK,
};

/**
 * Owns the right mouse button from press to release and decides which
 * of three gestures it was: a drag past the activation threshold is a
 * window/level drag, run by {@link WindowLevelDrag}; a
 * stationary click the active tool claimed is handed to the tool; and
 * any other stationary click is reported through
 * {@link onStationaryRightClick}.
 *
 * Follows the same pattern as {@link GlobalPanCoordinator}:
 * consumed exclusively by {@link ToolManager} — every mouse/key
 * handler delegates to this coordinator first and skips further
 * dispatch when the coordinator returns `true` (event consumed).
 *
 * ## Pointer lock
 *
 * Pointer lock is requested on mousedown (the only reliable
 * user-gesture context). The browser grants it asynchronously.
 *
 * - If the user drags past the activation threshold the cursor
 *   overlay appears and windowing begins.
 * - If the click is stationary the lock is released on mouseup.
 * - When the browser exits pointer lock on its own (Esc key or
 *   tab switch) it fires `pointerlockchange`. We detect this and
 *   restore the saved WW/WC, since the browser swallows the Esc
 *   keydown event in this case.
 * - When the lock ends because the pressed canvas left the page,
 *   the drag ends and keeps the WW/WC it reached.
 */
export class GlobalRightButtonCoordinator extends GlobalCoordinator {
    /** The window/level drag a press turns into once it moves. */
    private readonly windowing = new WindowLevelDrag();

    /** Whether the action button is currently held down. */
    private isActionButtonHeld = false;

    /** Whether the press has become a drag (moved past threshold). */
    private isDragging = false;

    /** Coordinates where the action button was pressed
     *  (clientX/clientY for threshold check and cursor overlay). */
    private startCoordinates = vec2.create();

    /** Accumulated movement since action button press
     *  (for threshold check). */
    private accumulatedMovement = vec2.create();

    /** Whether the browser denied pointer lock for the current drag. */
    private isPointerLockDenied = false;

    /**
     * Whether the active tool claimed the right button when this press
     * started (a line being finished, an annotation placement being
     * cancelled). Captured at the press, since the tool's action may end
     * before the release and the click belongs to whichever tool owned it
     * when it went down.
     */
    private rightButtonIsToolOwned = false;

    /** The view the press landed on, reported with a stationary click. */
    private pressView: PlaneView | undefined;

    /**
     * Called on a right-click that never became a drag, with the press
     * coordinates and the view pressed on. The press point is where the
     * user aimed: under pointer lock the release coordinates are frozen
     * at the lock position, and without the lock they may have drifted by
     * up to the activation threshold. The click stays unconsumed, so the
     * active tool receives it as well.
     */
    public onStationaryRightClick:
        | ((coordinates: vec2, view: PlaneView | undefined) => void)
        | undefined;

    /** Manages the Pointer Lock API lifecycle. */
    private pointerLockManager = new PointerLockManager({
        onExternalExit: () => {
            // The browser exited pointer lock externally (Esc
            // or tab/window switch). Only restore WW/WC if
            // the drag was active (post-threshold); pre-
            // threshold ESC and lock denial have no deltas to
            // undo. A lock that ended because the pressed
            // canvas left the page was not cancelled by the
            // user, so the drag ends where it is, as a release
            // would end it.
            if (this.isDragging && this.pressView?.canvas?.isConnected) {
                this.windowing.restoreWindowLevel();
            }
            this.deactivate();
            this.onCursorChanged();
        },
        onLockDenied: () => {
            // Pointer lock was denied (browser cooldown after
            // ESC). Windowing continues with free cursor
            // movement, so the system cursor must show again
            // or the user is left with an invisible cursor.
            // Not optimal, but still better UX than blocking
            // windowing level adjustments entirely until the
            // browser cooldown is over.
            this.isPointerLockDenied = true;
            this.onCursorChanged();
        },
    });

    /**
     * @param onCursorChanged Called when {@link cursor} changes outside
     *   an event dispatched by {@link ToolManager} — the browser ending
     *   or denying pointer lock — so the new value gets applied.
     */
    public constructor(private readonly onCursorChanged: () => void) {
        super();
    }

    // ───────── public API ─────────

    /**
     * Whether a window/level drag is in progress. A held press that has
     * not crossed the activation threshold does not count: it must stay
     * invisible to the rest of the system, so other presses are not
     * blocked and the crosshair keeps its mode, and the active tool still
     * receives the click if the press turns out to be stationary.
     */
    public get isActive(): boolean {
        return this.isDragging;
    }

    /**
     * The window/level drag's cursor, which hides the pointer behind its
     * overlay. Undefined once the browser has denied pointer lock: the
     * pointer then moves freely and must stay visible.
     */
    public get cursor(): string | undefined {
        return this.isPointerLockDenied ? undefined : this.windowing.cursor;
    }

    /**
     * Called by ToolManager.setActiveTool() to (re-)configure the
     * coordinator for the new tool. The window/level drag binds to the
     * pressed view's environment at each press, so nothing is bound here.
     */
    public initialize(): void {
        // Ends any gesture, and with it the windowing session, left over
        // from the previous tool.
        this.clearGestureState();
        // Releases the pointer lock and resets the manager, as deactivate
        // would, and also drops any orphaned listener from a previous
        // tool session.
        this.pointerLockManager.forceCleanUp();
    }

    /**
     * Right button handling for mouse press.
     * @returns true if the event was consumed.
     */
    public onMousePress(
        button: number,
        coordinates: vec2,
        view: PlaneView,
        modifierKeys: ModifierKeys,
        isStrokeInProgress: () => boolean,
        isPanActive: boolean,
        toolUsesButtonDuringAction: (button: number) => boolean
    ): boolean {
        if (
            button === RIGHT_BUTTON_CONFIG.mouseButton &&
            !isStrokeInProgress() &&
            !isPanActive &&
            !this.isDragging
        ) {
            this.isActionButtonHeld = true;
            // A denial from a previous press can arrive after that press
            // was released; it says nothing about this one.
            this.isPointerLockDenied = false;
            vec2.copy(this.startCoordinates, coordinates);
            vec2.zero(this.accumulatedMovement);

            this.windowing.begin(
                view.environment,
                modifierKeys,
                view.fusedImageDisplay
            );

            // Request pointer lock — mousedown is the only
            // reliable user-gesture context. If the click turns
            // out to be stationary we release on mouseup.
            this.pointerLockManager.setButtonHeld(true);
            const canvas = view.canvas ?? undefined;
            if (canvas) {
                this.pointerLockManager.requestLock(canvas);
            }

            // Show the neutral sun cursor immediately for early
            // visual feedback, unless the active tool uses
            // stationary right-click during its action (e.g.
            // LineDrawTool undo).
            this.rightButtonIsToolOwned = toolUsesButtonDuringAction(
                RIGHT_BUTTON_CONFIG.mouseButton
            );
            this.pressView = view;
            if (!this.rightButtonIsToolOwned) {
                this.windowing.showCursor(coordinates[0], coordinates[1]);
            }

            return true;
        }

        // Block all other mouse presses while the drag is active
        if (this.isDragging) {
            return true;
        }

        return false;
    }

    /**
     * Right button handling for mouse move.
     * @param movement Raw movementX/movementY from the
     *  MouseEvent.
     * @param buttons Current button bitmask.
     * @returns true if the event was consumed.
     */
    public onMouseMove(movement: vec2, buttons: number): boolean {
        // If action button was released while we weren't looking
        // (e.g. outside the window), clean up.
        if (
            this.isActionButtonHeld &&
            (buttons & RIGHT_BUTTON_CONFIG.mouseButtonMask) === 0
        ) {
            this.deactivate();
            return false;
        }

        if (!this.isActionButtonHeld) {
            return false;
        }

        if (!this.isDragging) {
            // Accumulate movement for threshold check
            this.accumulatedMovement[0] += movement[0];
            this.accumulatedMovement[1] += movement[1];

            const distance = vec2.length(this.accumulatedMovement);
            if (distance >= RIGHT_BUTTON_CONFIG.activationThreshold) {
                this.activate();
            } else {
                return true;
            }
        }

        this.windowing.applyDelta(movement);
        return true;
    }

    /**
     * Right button handling for mouse release.
     * @returns true if the event was consumed.
     */
    public onMouseRelease(button: number): boolean {
        if (button !== RIGHT_BUTTON_CONFIG.mouseButton) {
            // Block other button releases while active
            return this.isDragging;
        }

        if (this.isDragging) {
            this.deactivate();
            return true;
        }

        if (this.isActionButtonHeld) {
            // Stationary click — end the press, then let the tool
            // handle it.
            this.deactivate();
            // A right-click that never moved. Offered to the listener
            // only when the active tool did not claim the button for its
            // own action, so a line being finished cannot also open a
            // menu over the contour it just closed.
            if (!this.rightButtonIsToolOwned) {
                this.onStationaryRightClick?.(
                    vec2.clone(this.startCoordinates),
                    this.pressView
                );
            }
            return false;
        }

        return false;
    }

    /**
     * Esc key handling — fallback for when pointer lock is NOT
     * active. When pointer lock IS active the browser swallows
     * the Esc keydown to exit the lock; we detect that via the
     * pointer lock manager's onExternalExit callback instead.
     * @returns true if a window/level drag was active and Esc cancelled
     *   it, restoring the saved window level.
     */
    public onEscKey(): boolean {
        if (!this.isDragging) {
            return false;
        }

        // If pointer lock is active the browser will consume the
        // Esc keypress itself — this handler won't even run. But
        // guard just in case.
        if (this.pointerLockManager.isLocked) {
            return false;
        }

        this.windowing.restoreWindowLevel();
        this.deactivate();
        return true;
    }

    /**
     * Mouse leave handling — cancel pre-threshold state.
     */
    public onMouseLeave(): void {
        if (this.isActionButtonHeld && !this.isDragging) {
            this.deactivate();
        }
    }

    /**
     * Ends the gesture in progress, a press or a window/level drag, with
     * no release to end it: the window level stays where the drag took it,
     * the overlay is removed and the pointer lock is released. A press
     * that never moved is not reported as a stationary click.
     */
    public endGesture(): void {
        this.deactivate();
    }

    // ───────── private helpers ─────────

    /** Transition from pre-threshold to an active drag. */
    private activate(): void {
        this.isDragging = true;
        this.pointerLockManager.setActive(true);
        // Shows the overlay at the mousedown position unless the
        // pre-threshold feedback already put it there.
        this.windowing.showCursor(
            this.startCoordinates[0],
            this.startCoordinates[1]
        );
    }

    /** End the gesture, whether or not it became a drag, and release the pointer lock. */
    private deactivate(): void {
        // Clear flags first so that the pointerlockchange handler
        // (which fires asynchronously after releaseLock) sees
        // the coordinator as inactive and does not re-enter.
        this.clearGestureState();

        this.pointerLockManager.setActive(false);
        this.pointerLockManager.setButtonHeld(false);
        this.pointerLockManager.releaseLock();
    }

    /** Forget the press and any drag, and end the windowing session. */
    private clearGestureState(): void {
        this.isDragging = false;
        this.isActionButtonHeld = false;
        this.isPointerLockDenied = false;
        this.windowing.end();
    }
}
